import { styled } from '@linaria/react';
import dayjs from 'dayjs';
import type { RoadmapOutcome } from '../../../../api/strategy-client/strategy-client.type';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../../../ui-library/icon/icon';
import {
  calculateGridlinePositions,
  calculateMilestonePosition,
  calculateQuarterlinePositions,
} from '../roadmap.helpers';
import type { DeliveryProgressFilter } from '../roadmap.type';
import { LEFT_PADDING } from '../roadmap.type';
import { InitiativeBar } from './initiative-bar';
import { MilestoneLine } from './milestone-line';

interface OutcomeSectionProps {
  outcome: RoadmapOutcome;
  isExpanded: boolean;
  onToggle: () => void;
  timelineStart: dayjs.Dayjs;
  timelineEnd: dayjs.Dayjs;
  timelineWidth: string | number;
  totalDays: number;
  deliveryProgressFilter: DeliveryProgressFilter;
  selectedYear: number | null;
}

export function OutcomeSection({
  outcome,
  isExpanded,
  onToggle,
  timelineStart,
  timelineEnd,
  timelineWidth,
  totalDays,
  deliveryProgressFilter,
  selectedYear,
}: OutcomeSectionProps) {
  const i18n = useTranslation();
  const gridlines = selectedYear
    ? calculateGridlinePositions(timelineStart, timelineEnd, totalDays)
    : calculateQuarterlinePositions(timelineStart, timelineEnd, totalDays);
  const hasInitiatives = outcome.initiatives.length > 0;
  const outcomeProgress = typeof outcome.progress === 'number' ? outcome.progress : 0;

  // Calculate milestone positions
  const milestonePositions = (outcome.milestones || [])
    .map((milestone) => ({
      milestone,
      leftPercent: calculateMilestonePosition(milestone.target_end_date, timelineStart, totalDays),
    }))
    .filter((m) => m.leftPercent !== null);

  return (
    <OutcomeSectionStyled>
      <OutcomeSectionWrapper>
        <OutcomeRow>
          <OutcomeCard onClick={() => hasInitiatives && onToggle()}>
            <OutcomeCardHeader>
              <OutcomeType>{i18n.t('common.outcome', 'Objective')}</OutcomeType>
              {hasInitiatives && (
                <ExpandIcon>
                  <Icon
                    name={isExpanded ? 'expand_more' : 'chevron_right'}
                    size={18}
                    color="#2E7D32"
                    style={{ cursor: 'pointer' }}
                  />
                </ExpandIcon>
              )}
            </OutcomeCardHeader>
            <OutcomeCardBody>
              {outcome.key && <OutcomeCardKey>{outcome.key}</OutcomeCardKey>}
              <OutcomeCardProgress>{outcomeProgress.toFixed(1)}%</OutcomeCardProgress>
            </OutcomeCardBody>
            <OutcomeCardTitle>{outcome.name}</OutcomeCardTitle>
          </OutcomeCard>
          <OutcomeTimeline style={{ width: timelineWidth }}>
            {gridlines.map((gridline) => (
              <GridLine key={`outcome-${gridline.label}`} style={{ left: `${gridline.leftPercent}%` }} />
            ))}
          </OutcomeTimeline>
        </OutcomeRow>
        {isExpanded && hasInitiatives && (
          <MilestonesAndInitiativesContainer>
            {outcome.initiatives.map((initiative) => (
              <InitiativeBar
                key={initiative.id}
                initiative={initiative}
                timelineStart={timelineStart}
                timelineWidth={timelineWidth}
                totalDays={totalDays}
                deliveryProgressFilter={deliveryProgressFilter}
                selectedYear={selectedYear}
              />
            ))}
          </MilestonesAndInitiativesContainer>
        )}
        {isExpanded && milestonePositions.length > 0 && (
          <MilestoneOverlay>
            {milestonePositions.map(({ milestone, leftPercent }) => (
              <MilestoneLine key={`milestone-${milestone.id}`} milestone={milestone} leftPercent={leftPercent!} />
            ))}
          </MilestoneOverlay>
        )}
      </OutcomeSectionWrapper>
    </OutcomeSectionStyled>
  );
}

// Styled components
const OutcomeSectionStyled = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const OutcomeSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
`;

const OutcomeRow = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  background: var(--base-white);
`;

const OutcomeCard = styled.div`
  width: ${LEFT_PADDING}px;
  padding: 12px 14px;
  background: #e8f5e9;
  border: 2px solid #81c784;
  border-radius: 6px;
  margin: 10px 16px;
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: #c8e6c9;
    border-color: #66bb6a;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
  }
`;

const OutcomeCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const OutcomeType = styled.span`
  font-size: 10px;
  font-weight: 800;
  color: #2e7d32;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const ExpandIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OutcomeCardBody = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const OutcomeCardKey = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #388e3c;
`;

const OutcomeCardProgress = styled.span`
  font-size: 22px;
  font-weight: 700;
  color: #2e7d32;
`;

const OutcomeCardTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1b5e20;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const OutcomeTimeline = styled.div`
  border-left: 1px solid var(--sky-light);
  border-right: 1px solid var(--sky-light);
  flex: 1;
  position: relative;
  overflow: visible;
`;

const GridLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: var(--sky-light);
  opacity: 0.4;
  pointer-events: none;
`;

const MilestonesAndInitiativesContainer = styled.div`
  background: var(--base-white);
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const MilestoneOverlay = styled.div`
  position: absolute;
  top: 0;
  left: ${LEFT_PADDING}px;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 3;
`;
